//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { rankings, rankItem } from '@tanstack/match-sorter-utils';
import { FilterFn } from '@tanstack/react-table';
import { ReactComponent as ControllerIcon } from 'assets/controller-device.svg';
import { ReactComponent as EapCircleIcon } from 'assets/eap-circle.svg';
import { ReactComponent as EapIconCeilingMount } from 'assets/eap-device-ceiling-mount-round.svg';
import { ReactComponent as EapIconCeilingMountSquare2Ports } from 'assets/eap-device-ceiling-mount-square-2-ports.svg';
import { ReactComponent as EapDeviceGeneric } from 'assets/eap-device-generic.svg';
import { ReactComponent as EapIconOutdoor } from 'assets/eap-device-outdoor.svg';
import { ReactComponent as EapIconWallMount } from 'assets/eap-device-wall-mount.svg';
import { ReactComponent as EapRectangleIcon } from 'assets/eap-rectangle.svg';
import { ReactComponent as EapSquareIcon } from 'assets/eap-square.svg';
import qualityExcellent from 'assets/quality-excellent.svg';
import qualityGood from 'assets/quality-good.svg';
import qualityNormal from 'assets/quality-normal.svg';
import qualityPoor from 'assets/quality-poor.svg';
import { ReactComponent as SwitchIcon10Ports } from 'assets/switch-device-10-ports.svg';
import { ReactComponent as SwitchIcon26Ports } from 'assets/switch-device-26-ports.svg';
import { ReactComponent as SwitchIcon52Ports } from 'assets/switch-device-52-ports.svg';
import { ReactComponent as SwitchDeviceGeneric } from 'assets/switch-device-generic.svg';
import { ReactComponent as SwitchIcon } from 'assets/switch-icon.svg';
import { DeviceType } from 'dto/response-bin-info.dto';
import { ControllerQueryConfigDataDraft } from 'dto/update-controller-query-config-data.dto';
import { SiteType } from 'entities/site.entity';
import { t } from 'i18next';
import { DraftAction } from 'models/configuration-interface-ipv4-port-forward';
import { ControllerDeviceProperties } from 'models/controller-device-properties';
import { DeviceAttributesWithOutBitShifting } from 'models/device-attributes-without-bit-shifting';
import { EapDeviceProperties } from 'models/eap-device-properties';
import { SwitchDeviceProperties } from 'models/switch-device-properties';
import { TssAggregateAppNameTxRx } from 'models/tss-aggregate-app-name-tx-rx';
import { PieDataByAppName, TssDataByDevice } from 'models/tss-pie-data';
import { PortalRole } from 'pages/PortalProfile';
import { Role } from 'pages/Profile';
import { isValidElement } from 'react';
import { getItem } from './localStorageHelper';

export const loadImage = (path: () => void) => {
  try {
    return path();
  } catch (error) {
    return require('assets/not-found.png');
  }
};

export const dateToUnix = (dateString: string) => Math.floor(new Date(dateString).getTime() / 1000);

export const yTickFormatter = (decimal: number) => {
  if (decimal >= 35) {
    return t('helper.excellent');
  } else if (decimal >= 28 && decimal < 35) {
    return t('helper.good');
  } else if (decimal >= 20 && decimal < 28) {
    return t('helper.normal');
  } else {
    return t('helper.poor');
  }
};

export const transferSnrImg = (val: number, isWiredConnect: boolean = false) => {
  if (Number(val) >= 35 || isWiredConnect) {
    return { src: qualityExcellent, alt: 'excellent' };
  }
  if (Number(val) >= 28 && Number(val) < 35) {
    return { src: qualityGood, alt: 'good' };
  }
  if (Number(val) >= 20 && Number(val) < 28) {
    return { src: qualityNormal, alt: 'normal' };
  }
  if (Number(val) > 0 && Number(val) < 20) {
    return { src: qualityPoor, alt: 'poor' };
  }
  return { src: qualityPoor, alt: 'poor' };
};

type QoeVersion = 'ai' | 'formula';
export const transferQoeQuality = (val: number, version: QoeVersion = 'ai') => {
  // 處理無效數值
  if (val === null || val === undefined || isNaN(val)) {
    return { color: 'text-gray-200', text: '--' };
  }

  if (version === 'formula') {
    if (val >= 800) return { color: 'text-cyfi-excellent', text: t('helper.excellent') };
    if (val >= 600) return { color: 'text-cyfi-good', text: t('helper.good') };
    if (val >= 551) return { color: 'text-cyfi-normal', text: t('helper.normal') };
    return { color: 'text-cyfi-poor', text: t('helper.poor') }; // 0~550
  } else {
    if (val >= 900) return { color: 'text-cyfi-excellent', text: t('helper.excellent') };
    if (val >= 800) return { color: 'text-cyfi-good', text: t('helper.good') };
    if (val >= 600) return { color: 'text-cyfi-normal', text: t('helper.normal') };
    return { color: 'text-cyfi-poor', text: t('helper.poor') }; // 0~599
  }
};

export const mapConnectType = (connectType: number, showGHz: boolean = true, showWired: boolean = true) => {
  const suffix = showGHz ? t('helper.GHz') : '';

  if (connectType === 0 && !showWired) {
    return '';
  }

  switch (connectType) {
    case 0: {
      return t('helper.mapConnectTypeWired');
    }
    case 1: {
      return t('helper.mapConnectType24G') + suffix;
    }
    case 2: {
      return t('helper.mapConnectType5G') + suffix;
    }
    case 5: {
      return t('helper.mapConnectType6G') + suffix;
    }
    case 99: {
      return t('helper.mapConnectTypeMlo');
    }
    default: {
      return '';
    }
  }
};

export const getRoleText = (role: string) => {
  switch (role) {
    case PortalRole.Root:
      return t('portalProfile.userRoleRoot');
    case PortalRole.Admin:
      return t('portalProfile.userRoleAdministrator');
    case Role.Owner:
      return t('profile.memberRoleOwner');
    case Role.Manager:
      return t('profile.memberRoleManager');
    case Role.Viewer:
      return t('profile.memberRoleViewer');
    default: {
      return '';
    }
  }
};

export const transferUnitByte = (byte: number, digit: number): { unit: 'TB' | 'GB' | 'MB' | 'KB' | 'B'; value: string } => {
  if (digit > 0 ? byte >= Math.pow(1000, 3) * 100 : byte >= Math.pow(1000, 4)) {
    return { unit: 'TB', value: (byte / Math.pow(1000, 4)).toFixed(digit) };
  } else if (digit > 0 ? byte >= Math.pow(1000, 2) * 100 : byte >= Math.pow(1000, 3)) {
    return { unit: 'GB', value: (byte / Math.pow(1000, 3)).toFixed(digit) };
  } else if (digit > 0 ? byte >= 1000 * 100 : byte >= Math.pow(1000, 2)) {
    return { unit: 'MB', value: (byte / Math.pow(1000, 2)).toFixed(digit) };
  } else if (digit > 0 ? byte >= 100 : byte >= 1000) {
    return { unit: 'KB', value: (byte / 1000).toFixed(digit) };
  } else {
    return { unit: 'B', value: byte.toFixed(0) };
  }
};

export const transferUnitBps = (
  bpsVal: number,
  digit: number
): { unit: 'bps' | 'Kbps' | 'Mbps' | 'Gbps' | 'Tbps'; unitNum: number; value: string } => {
  if (digit > 0 ? bpsVal >= Math.pow(1000, 3) * 100 : bpsVal >= Math.pow(1000, 4)) {
    return { unit: 'Tbps', unitNum: Math.pow(1000, 4), value: (bpsVal / Math.pow(1000, 4)).toFixed(digit) };
  } else if (digit > 0 ? bpsVal >= Math.pow(1000, 2) * 100 : bpsVal >= Math.pow(1000, 3)) {
    return { unit: 'Gbps', unitNum: Math.pow(1000, 3), value: (bpsVal / Math.pow(1000, 3)).toFixed(digit) };
  } else if (digit > 0 ? bpsVal >= 1000 * 100 : bpsVal >= Math.pow(1000, 2)) {
    return { unit: 'Mbps', unitNum: Math.pow(1000, 2), value: (bpsVal / Math.pow(1000, 2)).toFixed(digit) };
  } else if (digit > 0 ? bpsVal >= 100 : bpsVal >= 1000) {
    return { unit: 'Kbps', unitNum: 1000, value: (bpsVal / 1000).toFixed(digit) };
  } else {
    return { unit: 'bps', unitNum: 1, value: bpsVal.toFixed(0) };
  }
};

export const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({
    itemRank,
  });

  return itemRank.passed;
};

// 用來覆蓋搜尋用的 filterValue，讓使用者可以搜尋畫面上看到的文字
export const displayValueContainsFilter =
  <TData,>(filterValueMap?: Partial<Record<string, (row: TData) => unknown>>): FilterFn<TData> =>
  (row, columnId, value, addMeta) => {
    /** NOTE: 依照 ranking.CONTAINS 來過濾資料 */
    const filterKeyword = typeof value === 'object' && value !== null && 'value' in value ? value.value : value;
    const filterValue = filterValueMap?.[columnId]?.(row.original) ?? row.getValue(columnId);
    const itemRank = rankItem(filterValue, filterKeyword, { threshold: rankings.CONTAINS });
    addMeta({
      itemRank,
    });

    return itemRank.passed;
  };

export const containsFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  /** NOTE: 依照 ranking.CONTAINS 來過濾資料 */
  const itemRank = rankItem(row.getValue(columnId), value, { threshold: rankings.CONTAINS });
  addMeta({
    itemRank,
  });

  return itemRank.passed;
};

export const transferUnitSec = (seconds: number) => {
  let daySec = 24 * 60 * 60;
  let hourSec = 60 * 60;
  let minuteSec = 60;
  let day = Math.floor(seconds / daySec);
  let hour = Math.floor((seconds % daySec) / hourSec);
  let min = Math.floor((seconds % hourSec) / minuteSec);
  let sec = seconds % minuteSec;
  let dayUnit = day > 1 ? t('helper.transferUnitSecDays') : t('helper.transferUnitSecDay');
  let hourUnit = hour > 1 ? t('helper.transferUnitSecHours') : t('helper.transferUnitSecHour');
  let minUnit = min > 1 ? t('helper.transferUnitSecMins') : t('helper.transferUnitSecMin');
  let secUnit = sec > 1 ? t('helper.transferUnitSecSecs') : t('helper.transferUnitSecSec');

  if (day > 0) {
    return day + dayUnit + hour + hourUnit + min + minUnit;
  } else if (hour > 0) {
    return hour + hourUnit + min + minUnit;
  } else if (min > 0) {
    return min + minUnit;
  } else {
    return sec + secUnit;
  }
};

export const formatLoadAverage = (loadAverage: unknown): string => {
  if (typeof loadAverage !== 'string' || loadAverage.trim() === '') return '--';

  const value = Number.parseFloat(loadAverage.trim().split(/\s+/)[0]);
  return Number.isFinite(value) ? `${(value * 100).toFixed(0)} %` : '--';
};

export const mergeChartOneYData = (yData: number[], keyName: string) =>
  yData.map((c) => ({
    [keyName]: c,
  }));

export const transferUnitKpsData = (yData: number[], unitNum: number, digit: number) => yData.map((c) => parseFloat((c / unitNum).toFixed(digit)));

export const findMaxNum = (yData: number[]) => {
  let max_num = yData[0];
  yData.forEach((entry, index) => {
    if (max_num < yData[index]) {
      max_num = yData[index];
    }
  });
  return max_num;
};

export const chartData = (duration: number, inputData: { [index: string]: number | string }[], dateStart: number, xDataName: 'day' | 'time') => {
  let i = 0;
  let time = dateStart;
  const data: { [index: string]: number | string }[] = [];
  for (i = 0; i < inputData.length; i++) {
    if (i !== 0) {
      time = time + duration;
    }
    if (i === inputData.length - 1) {
      data.push({
        ...inputData[i],
        xData: t('helper.now'),
        time: time,
      });
    } else {
      data.push({
        ...inputData[i],
        xData: getTime(xDataName, time),
        time: time,
      });
    }
  }
  return data;
};

export const timeTypeInterval = {
  day: 5 * 60, // 5 分鐘
  week: 60 * 60, // 1 小時
  month: 12 * 60 * 60, // 12 小時
};

/**
 * @param fromTo
 * @param {object} options
 * @property {string} timeType - 時間圖表的類型 day / week / month
 * @property {boolean} chartTime - 指定要畫的圖表的時間；若沒指定則會用當下時間去畫時間軸
 * @property {array} sourceData - 來源資料數據；用來確認圖表時間與來源資料內的時間是否有誤差
 */
export const getChartTimeInfo = ({
  timeType,
  chartTime,
  sourceData,
}: {
  timeType: 'day' | 'week' | 'month';
  chartTime?: number;
  sourceData?: { [x: string]: number | string }[];
}) => {
  const oneDayInSeconds = 24 * 60 * 60;
  let nowInSeconds = chartTime ?? Math.floor(Date.now() / 1000);
  /* month => 12 小時 ； week => 1 小時 ; day => 5 分鐘 */
  const interval = timeTypeInterval[timeType];
  /* month => 30 天 ； week => 7 天 ; day => 1 天 */
  const scope = timeType === 'month' ? 30 : timeType === 'week' ? 7 : 1;
  let endTime = nowInSeconds - (nowInSeconds % interval);
  let startTime = endTime - scope * oneDayInSeconds + interval;

  /* 因為前端與後端都是各自去處理時間 */
  /* 防止前端在發 API request 之前或之後拿時間時，造成與後端時間誤差了一個時間點 */
  if (chartTime === undefined && Array.isArray(sourceData)) {
    const timestamps = sourceData.map(({ timestamp }) => toNumber(timestamp));
    const minTimestamp = Math.min(...timestamps);
    const maxTimestamp = Math.max(...timestamps);
    if (minTimestamp > 0 && maxTimestamp > 0) {
      if (minTimestamp < startTime) {
        nowInSeconds -= interval;
        startTime -= interval;
        endTime -= interval;
      }
      if (maxTimestamp > endTime) {
        nowInSeconds += interval;
        startTime += interval;
        endTime += interval;
      }
    }
  }

  return {
    chartTime: nowInSeconds,
    startTime,
    endTime,
    interval,
  };
};

/**
 * @param fromTo
 * @param {object} options
 * @property {array} sourceData - 來源資料數據
 * @property {string} timeType - 時間圖表的類型 day / week / month
 * @property {array} fromTo - 例如 [['X', 'Y'], ['A', 'B']] 將來源資料 [X] 跟 [A] 轉儲到圖表時間點資料 [Y] 跟 [B] 裡
 * @property {string[]} transferValue - 轉換圖表上欲呈現的數值
 * @property {boolean} chartTime - 指定要畫的圖表的時間；若沒指定則會用當下時間去畫時間軸
 */
export const generateChartDataFromTimestampData = ({
  sourceData,
  fromTo = [],
  timeType,
  transferValue,
  chartTime,
}: {
  sourceData: { [x: string]: number | string }[];
  fromTo?: ([string, string] | [string, string, (value: number) => number | string])[];
  timeType: 'day' | 'week' | 'month';
  transferValue?: (value: number) => number;
  chartTime?: number;
}) => {
  const transferValueHandler = transferValue ? transferValue : (value: number) => value;
  const chartTimeInfo = getChartTimeInfo(chartTime === undefined ? { timeType, sourceData } : { timeType, chartTime });
  const { startTime, endTime, interval } = chartTimeInfo;
  chartTime = chartTimeInfo.chartTime;

  const aggregate: { [x: string]: number } = {};

  const chartNodeMap = new Map<number, { [x: string]: number | string }>();
  /* 將來源資料轉換成時間點的資料 */
  const setNodeInfo = (time: number, dataItem: { [x: string]: number | string } = {}) => {
    const nodeInfo: { [x: string]: number | string } = {
      ...dataItem,
      xData: time === endTime ? t('helper.now') : getTime(timeType === 'day' ? 'day' : 'time', time),
      time,
    };
    fromTo.forEach(([from, to, transferTo]) => {
      const fromValue = toNumber(dataItem[from]);
      aggregate[from] = (aggregate[from] ?? 0) + fromValue;
      nodeInfo[to] = (transferTo ?? transferValueHandler)(fromValue);
    });
    chartNodeMap.set(time, nodeInfo);
  };

  /* 製作圖表每個時間點的資料 */
  for (let nodeTime = startTime; nodeTime <= endTime; nodeTime += interval) {
    setNodeInfo(nodeTime);
  }

  /* 將每筆的來源資料轉儲到圖表中對應的時間點上 */
  sourceData.forEach((dataItem) => {
    const time = Number(dataItem.timestamp);
    if (time % interval === 0 && time >= startTime && time <= endTime) {
      setNodeInfo(time, dataItem);
    }
  });

  return {
    chartTime,
    startTime,
    endTime,
    interval,
    chartData: Array.from(chartNodeMap.values()),
    aggregate,
  };
};

export const getTime = (type: 'day' | 'time', time: number) => {
  let a = new Date(time * 1000);
  let months = [
    t('helper.monthJan'),
    t('helper.monthFeb'),
    t('helper.monthMar'),
    t('helper.monthApr'),
    t('helper.monthMay'),
    t('helper.monthJun'),
    t('helper.monthJul'),
    t('helper.monthAug'),
    t('helper.monthSep'),
    t('helper.monthOct'),
    t('helper.monthNov'),
    t('helper.monthDec'),
  ];
  let year = a.getFullYear();
  let month = months[a.getMonth()];
  let date = a.getDate();
  let hours = a.getHours();
  let minutes = a.getMinutes().toString(10).padStart(2, '0');
  let formattedTime = '';

  if (type === 'day') {
    formattedTime = hours + ':' + minutes;
  } else {
    formattedTime = `${month} ${date} ${year}, ${hours}:${minutes}`;
  }
  return formattedTime;
};

export const randomColor = () =>
  Math.floor(Math.random() * 16777215)
    .toString(16)
    .padEnd(6, '0');

export const fixedChartColorList: string[] = [
  '#DC9FB4',
  '#FFE994',
  '#A8D8B9',
  '#58B2DC',
  '#D34778',
  '#FFC408',
  '#A4C2AA',
  '#1D88A8',
  '#AB3B3A',
  '#E8B647',
  '#00896C',
  '#006284',
  '#D0104C',
  '#AEB16C',
  '#2D6D4B',
  '#08192D',
  '#9F353A',
  '#CDE4AE',
  '#A5DEE4',
  '#6982B7',
  '#FBD095',
  '#86C166',
  '#66BAB7',
  '#005CAF',
  '#F6AC49',
  '#4A593D',
  '#33A6B8',
  '#986DB2',
  '#F05E1C',
  '#24483A',
  '#0D5661',
  '#572A3F',
];

export const fixedChartColorPicker = (index: number): string => fixedChartColorList[index % fixedChartColorList.length];

export const mapMemberStatusColor = (status: string) => {
  switch (status) {
    case 'invited': {
      return 'text-blue-400';
    }
    case 'pending': {
      return 'text-[#FBA919]';
    }
    case 'active': {
      return 'text-[#8AC640]';
    }
    default: {
      return '';
    }
  }
};

export const mapMemberStatusString = (status: string) => {
  switch (status) {
    case 'invited': {
      return t('portalMember.statusInvited');
    }
    case 'pending': {
      return t('portalMember.statusPending');
    }
    case 'active': {
      return t('portalMember.statusActive');
    }
    default: {
      return '';
    }
  }
};

// NOTE: format is YYYY-MM-DD HH:mm
export const dateTimeFormating = (time: number) => {
  const date = new Date(time);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// NOTE: format is HHss -> HH:ss
export const formatTime = (num: number): string => {
  const hours = Math.floor(num / 100);
  const minutes = num % 100;
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

/** 整數 HHmm → Date */
export const toDate = (value: number): Date => {
  const hours = Math.floor(value / 100);
  const minutes = value % 100;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

/** Date → HHmm 整數 */
export const dateToInt = (date: Date): number => {
  return date.getHours() * 100 + date.getMinutes();
};

export function isBitmapMatch(value: number, matchBits: number, isExactMatch: boolean = false): boolean {
  const maskedValue = Number.isInteger(value) && Number.isInteger(matchBits) ? value & matchBits : 0;
  return isExactMatch ? maskedValue === matchBits : maskedValue !== 0;
}

export function setBitmapBit(value: number, matchBits: number, isSet: boolean): number {
  if (!Number.isInteger(value) || !Number.isInteger(matchBits)) return value;

  // 如果 isSet 為 true，執行位元 OR (|) 將位元設為 1
  // 如果 isSet 為 false，執行位元 AND (&) 配合反轉 (~) 將位元清為 0
  return isSet ? value | matchBits : value & ~matchBits;
}

export const isDevAttrsMatch = (devAttrs: number, matchAttrs: number): boolean => {
  return isBitmapMatch(devAttrs, matchAttrs);
};

export const getDeviceIcon = ({
  deviceType,
  type,
  totalPortQuantity = 0,
  className = 'text-cyfi-hover dark:text-sonicfi-islandParadise',
}: {
  deviceType: DeviceType;
  type?: string;
  totalPortQuantity?: number;
  className?: string;
}) => {
  if (deviceType === DeviceType.Eap) {
    switch (type) {
      case 'eap-cellingMount':
        return totalPortQuantity > 1 ? <EapSquareIcon className={className} /> : <EapCircleIcon className={className} />;
      case 'eap-wallMount':
        return <EapRectangleIcon className={className} />;
      case 'eap-outdoor':
        return <EapRectangleIcon className={className} />;
      default:
        return <EapCircleIcon className={className} />;
    }
  } else if (deviceType === DeviceType.Switch) {
    return <SwitchIcon className={className} />;
  }
};
export const getDeviceImg = ({
  devAttrs,
  devProps,
  configDevAttr,
  isDrawer = false,
}: {
  devAttrs: number;
  devProps: ControllerDeviceProperties | EapDeviceProperties | SwitchDeviceProperties;
  configDevAttr: DeviceAttributesWithOutBitShifting;
  isDrawer?: boolean;
}) => {
  if (isDevAttrsMatch(devAttrs, configDevAttr.TypeController)) {
    /* default use Controller Basic Router */
    return isDrawer ? (
      <ControllerIcon className="w-48 h-16" data-testid="ctl-icon" />
    ) : (
      <ControllerIcon className="w-full h-full p-1.5" data-testid="ctl-icon" />
    );
  } else if (isDevAttrsMatch(devAttrs, configDevAttr.TypeEap)) {
    if (isDevAttrsMatch(devAttrs, configDevAttr.SubtypeEapOutdoor)) {
      return isDrawer ? (
        <EapIconOutdoor className="w-20 h-20" data-testid="outdoor-eap-icon" />
      ) : (
        <EapIconOutdoor className="w-full h-full px-4" data-testid="outdoor-eap-icon" />
      );
    } else if (isDevAttrsMatch(devAttrs, configDevAttr.SubtypeEapWallMount)) {
      return isDrawer ? (
        <EapIconWallMount className="w-20 h-20" data-testid="wall-mount-eap-icon" />
      ) : (
        <EapIconWallMount className="w-full h-full px-4" data-testid="wall-mount-eap-icon" />
      );
    } else if (isDevAttrsMatch(devAttrs, configDevAttr.SubtypeEapCeilingMount)) {
      if (toNumber(devProps.totalPortQuantity) > 1) {
        return isDrawer ? (
          <EapIconCeilingMountSquare2Ports className="w-20 h-20" data-testid="ceiling-mount-square-2ports-eap-icon" />
        ) : (
          <EapIconCeilingMountSquare2Ports className="w-full h-full px-2" data-testid="ceiling-mount-square-2ports-eap-icon" />
        );
      }
      return isDrawer ? (
        <EapIconCeilingMount className="w-20 h-20" data-testid="ceiling-mount-eap-icon" />
      ) : (
        <EapIconCeilingMount className="w-full h-full px-2" data-testid="ceiling-mount-eap-icon" />
      );
    }
    /* default use EAP device generic icon */
    return isDrawer ? (
      <EapDeviceGeneric className="w-20 h-20" data-testid="eap-device-generic" />
    ) : (
      <EapDeviceGeneric className="w-full h-full max-h-28" data-testid="eap-device-generic" />
    );
  } else if (isDevAttrsMatch(devAttrs, configDevAttr.TypeSwitch)) {
    if (isDevAttrsMatch(devAttrs, configDevAttr.SwitchType24Port)) {
      return isDrawer ? (
        <SwitchIcon26Ports className="w-72 h-24" data-testid="26ports-switch-icon" />
      ) : (
        <SwitchIcon26Ports className="w-full h-full p-2" data-testid="26ports-switch-icon" />
      );
    } else if (isDevAttrsMatch(devAttrs, configDevAttr.SwitchType48Port)) {
      return isDrawer ? (
        <SwitchIcon52Ports className="w-72 h-24" data-testid="52ports-switch-icon" />
      ) : (
        <SwitchIcon52Ports className="w-full h-full p-2" data-testid="52ports-switch-icon" />
      );
    } else if (isDevAttrsMatch(devAttrs, configDevAttr.SwitchType8Port)) {
      return isDrawer ? (
        <SwitchIcon10Ports className="w-72 h-24" data-testid="10ports-switch-icon" />
      ) : (
        <SwitchIcon10Ports className="h-full p-2" data-testid="10ports-switch-icon" />
      );
    }

    /* default use Switch device generic icon */
    return isDrawer ? (
      <SwitchDeviceGeneric className="w-72 h-24" data-testid="switch-device-generic" />
    ) : (
      <SwitchDeviceGeneric className="w-full h-full p-2" data-testid="switch-device-generic" />
    );
  }
};

export const getSwitchType = ({
  devAttrs,
  devProps,
  configDevAttr,
  isDrawer = false,
}: {
  devAttrs: number;
  devProps: SwitchDeviceProperties;
  configDevAttr: DeviceAttributesWithOutBitShifting;
  isDrawer?: boolean;
}) => {
  const { portQuantity = 0, sfpPortQuantity = 0, sfpPortStartPortNumber = 0, sfpPortEndPortNumber = 0 } = devProps;
  const portRow = portQuantity < 16 ? 1 : 2;
  const portSet = portQuantity / portRow / 4;
  const sftPort = sfpPortQuantity / portRow;
  let wanSfpNum: number[] = [];
  if (sfpPortStartPortNumber > 0 && sfpPortEndPortNumber > 0) {
    /* 產生 SFP Port Number 範圍陣列；例如：[25, 26] */
    wanSfpNum = Array.from({ length: sfpPortEndPortNumber - sfpPortStartPortNumber + 1 }, (_, a) => a + sfpPortStartPortNumber);
  }
  if (isDevAttrsMatch(devAttrs, configDevAttr.SwitchType24Port) || isDevAttrsMatch(devAttrs, configDevAttr.SwitchType48Port)) {
    return {
      img: getDeviceImg({ devAttrs, devProps, configDevAttr, isDrawer }),
      appearance: { portRow, up4PortSet: portSet, down4PortSet: portSet, upSfpPort: sftPort, downSfpPort: sftPort, wanSfpNum },
    };
  } else if (isDevAttrsMatch(devAttrs, configDevAttr.SwitchType8Port)) {
    return {
      img: getDeviceImg({ devAttrs, devProps, configDevAttr, isDrawer }),
      appearance: { portRow, up4PortSet: 0, down4PortSet: portSet, upSfpPort: 0, downSfpPort: sftPort, wanSfpNum },
    };
  } else {
    return {
      img: getDeviceImg({ devAttrs, devProps, configDevAttr, isDrawer }),
      appearance: { portRow, up4PortSet: 0, down4PortSet: portSet, upSfpPort: 0, downSfpPort: sftPort, wanSfpNum },
    };
  }
};

export const isSwitchPoePlusPort = (portNumber: number, devProps: SwitchDeviceProperties) => {
  const { poePlusStartPortNumer = 0, poePlusEndPortNumer = 0 } = devProps;
  return (
    poePlusStartPortNumer > 0 &&
    poePlusEndPortNumer > 0 &&
    poePlusEndPortNumer - poePlusStartPortNumer + 1 > 0 &&
    portNumber >= poePlusStartPortNumer &&
    portNumber <= poePlusEndPortNumer
  );
};

export const isSwitchPoePlusPlusPort = (portNumber: number, devProps: SwitchDeviceProperties) => {
  const { poePlusPlusStartPortNumer = 0, poePlusPlusEndPortNumer = 0 } = devProps;
  return (
    poePlusPlusStartPortNumer > 0 &&
    poePlusPlusEndPortNumer > 0 &&
    poePlusPlusEndPortNumer - poePlusPlusStartPortNumer + 1 > 0 &&
    portNumber >= poePlusPlusStartPortNumer &&
    portNumber <= poePlusPlusEndPortNumer
  );
};

export const getControllerType = ({
  devAttrs,
  devProps,
  configDevAttr,
  isDrawer = false,
}: {
  devAttrs: number;
  devProps: ControllerDeviceProperties;
  configDevAttr: DeviceAttributesWithOutBitShifting;
  isDrawer?: boolean;
}) => {
  const { wanPortQuantity = 0, lanPortQuantity = 0 } = devProps;
  const portSet = lanPortQuantity / 4;
  const wanNum = Array.from({ length: wanPortQuantity }, (_, a) => a + 1);

  if (isDevAttrsMatch(devAttrs, configDevAttr.SubtypeControllerBasicRouter)) {
    return {
      img: getDeviceImg({ devAttrs, devProps, configDevAttr, isDrawer }),
      appearance: { portRow: 1, down4PortSet: portSet, down1Port: wanPortQuantity, wanNum },
    };
  } else {
    return {
      img: getDeviceImg({ devAttrs, devProps, configDevAttr, isDrawer }),
      appearance: { portRow: 1, down4PortSet: portSet, down1Port: wanPortQuantity, wanNum },
    };
  }
};

export const getEapType = ({
  devAttrs,
  devProps,
  configDevAttr,
  isDrawer = false,
}: {
  devAttrs: number;
  devProps: EapDeviceProperties;
  configDevAttr: DeviceAttributesWithOutBitShifting;
  isDrawer?: boolean;
}) => {
  const { wanPortQuantity = 0, lanPortQuantity = 0 } = devProps;
  const portSet = lanPortQuantity / 4;
  const totalPortQuantity = wanPortQuantity + lanPortQuantity;

  return {
    img: getDeviceImg({ devAttrs, devProps, configDevAttr, isDrawer }),
    appearance: { down4PortSet: portSet, totalPortQuantity },
  };
};

export const isEapPoePlusPort = (portNumber: number, devProps: SwitchDeviceProperties) => {
  const { poePlusStartPortNumer = 0, poePlusEndPortNumer = 0 } = devProps;
  return (
    poePlusStartPortNumer > 0 &&
    poePlusEndPortNumer > 0 &&
    poePlusEndPortNumer - poePlusStartPortNumer + 1 > 0 &&
    portNumber >= poePlusStartPortNumer &&
    portNumber <= poePlusEndPortNumer
  );
};

export const PORT_SPEED_COLORS: Record<string, string> = {
  '10G': '#9798C6',
  '5G': '#8D9EC5',
  '2.5G': '#87A4CB',
  '1G': '#81A9D1',
  '100M': '#CDDD55',
  '10M': '#DAE67F', // 10 Base-T
};

export const PORT_DISCONNECTED_COLOR = '#F0F0F0';

export const PORT_SELECT_COLOR = '#365069';

export const getPortSpeedTextColor = (speed: string): string => {
  switch (speed) {
    case '10G':
      return 'text-[#9798C6]';
    case '5G':
      return 'text-[#8D9EC5]';
    case '2.5G':
      return 'text-[#87A4CB]';
    case '1G':
      return 'text-[#81A9D1]';
    case '100M':
      return 'text-[#CDDD55]';
    case '10M':
      return 'text-[#DAE67F]';
    default:
      return '';
  }
};

export const COLORS = ['#FF6961', '#FFB480', '#F8F38D', '#08CAD1', '#59ADF6', '#42D6A4', '#9D94FF', '#C780E8'];

export const sumTssByDevice = ({
  data,
  topNum,
  withPercentage,
  keyArray = ['name', 'tx', 'rx'],
}: {
  data: any[];
  topNum: number;
  withPercentage: boolean;
  keyArray?: string[];
}) => {
  /*去除重複資料避免圖畫不出來*/
  const set = new Set();
  const noDuplicateData = data.filter((item) => (!set.has(item[keyArray[0]]) ? set.add(item[keyArray[0]]) : false));
  let totalSum = 0,
    topTotalSum = 0;
  /*計算每一筆data的total */
  let tssDataWithTotal: TssDataByDevice[] = noDuplicateData.map((d) => {
    totalSum += d[keyArray[1]] + d[keyArray[2]];
    return Object.assign({ name: d[keyArray[0]], tx: d[keyArray[1]], rx: d[keyArray[2]], total: d[keyArray[1]] + d[keyArray[2]] });
  });
  /*依照total排序data */
  tssDataWithTotal.sort(function (a, b) {
    return b.total - a.total;
  });
  /*取出指定top的資料 */
  const topTssDataWithTotal = tssDataWithTotal.slice(0, topNum);
  /*計數top資料的總數 */
  topTssDataWithTotal.forEach((d) => {
    topTotalSum += d.total;
  });
  /*總數單位轉換 */
  const topSumTransferUnitByte = transferUnitByte(topTotalSum, 2);
  /*若需要百分比資料，則加入並回傳 */
  let finalData = topTssDataWithTotal;
  if (withPercentage) {
    finalData = topTssDataWithTotal.map((d) => {
      return Object.assign(d, { percentage: totalSum > 0 ? Number(((d.total / totalSum) * 100).toFixed(0)) : 0 });
    });
  }
  return { finalData, topSumTransferUnitByte };
};

export const sumTssByAppName = ({
  data,
  type,
  topNum,
  unknownAction,
}: {
  data: TssAggregateAppNameTxRx[];
  type: 'total' | 'up' | 'down';
  topNum: number;
  unknownAction: 'filter' | 'other'; // judge "__unknown" data should filter out or combine into others
}) => {
  let totalSum = 0,
    txSum = 0,
    rxSum = 0,
    sliceIdx = -1;
  let _data: TssAggregateAppNameTxRx[] = data;
  let finalPieData: PieDataByAppName[] = [];
  let othersPieData: PieDataByAppName = {
    appName: 'others',
    tx: 0,
    rx: 0,
    total: 0,
  };

  if (unknownAction === 'filter') {
    _data = data.filter((d) => d.appName !== '__unknown');
  }

  let pieData: PieDataByAppName[] = _data.map((d) => {
    txSum += d.tx;
    rxSum += d.rx;
    totalSum += d.tx + d.rx;
    return Object.assign(d, { total: d.tx + d.rx });
  });

  /*if "__unknown" data should combine into others, find out __unknown -> add to others, then filter it out in pieData */
  if (unknownAction === 'other') {
    const unknownData = pieData.find((p) => p.appName === '__unknown');
    if (unknownData) {
      othersPieData.tx += unknownData.tx;
      othersPieData.rx += unknownData.rx;
      othersPieData.total += unknownData.total;
    }
    pieData = pieData.filter((d) => d.appName !== '__unknown');
  }

  const sumTransferUnitByte =
    type === 'total' ? transferUnitByte(totalSum, 2) : type === 'up' ? transferUnitByte(txSum, 2) : transferUnitByte(rxSum, 2);

  pieData.sort(function (a, b) {
    if (type === 'total') {
      return b.total - a.total;
    } else if (type === 'up') {
      return b.tx - a.tx;
    } else {
      return b.rx - a.rx;
    }
  });

  pieData.forEach((d, idx) => {
    const percent =
      type === 'total'
        ? parseInt(((d.total / totalSum) * 100).toFixed(0))
        : type === 'up'
          ? parseInt(((d.tx / txSum) * 100).toFixed(0))
          : parseInt(((d.rx / rxSum) * 100).toFixed(0));
    if (idx > topNum - 1 && percent <= 0) {
      if (sliceIdx === -1) sliceIdx = idx;
      othersPieData.tx += d.tx;
      othersPieData.rx += d.rx;
      othersPieData.total += d.total;
    }
  });

  finalPieData = sliceIdx > 0 ? pieData.slice(0, sliceIdx) : pieData;

  if (othersPieData.total > 0) {
    finalPieData.push(othersPieData);
  }

  return { sortOutData: finalPieData, sumTransferUnitByte };
};

export const xtickFormatter = (time: number) => {
  const diff = Math.floor(Date.now() / 1000) - time;
  if (diff < 1000) {
    return t('helper.now');
  } else if (diff < 24 * 60 * 60 + 1000) {
    return getTime('day', time);
  } else {
    return getTime('time', time);
  }
};

export const initChartData = (duration: number, interval: number) => {
  const currentTime = Math.floor(Date.now() / 1000);
  const endTime = currentTime - (currentTime % duration);
  const startTime = endTime - interval + duration;
  const nodeSize = interval / duration;
  const timestamps = Array.from({ length: nodeSize }, (_, i) => startTime + duration * i);
  return Object.fromEntries(timestamps.map((timestamp) => [timestamp, 0]));
};

/** 當 NaN 時給預設值，避免 NaN 的情況 */
export function toNumber(value: any, defaultValue: number = 0) {
  /* 不能用 `Number(value) || defaultValue` 的寫法，雖然 NaN 在 || 下會成立，但若剛好 `Number(value)` 的值為 0 也會促使 || 成立 */
  // Number('') will be 0 - 會造成非預期的結果，正確應該要是 defaultValue
  // Number(null) will be 0 - 會造成非預期的結果，正確應該要是 defaultValue
  // Number([123]) will be 123 - 會造成非預期的結果，正確應該要是 defaultValue
  let num: number;
  if (typeof value === 'number') {
    num = value;
  } else if (typeof value === 'string') {
    num = value === '' ? defaultValue : Number(value);
  } else {
    num = defaultValue;
  }
  return isNaN(num) ? defaultValue : num;
}

export const BuildVersionList = {
  controller: 'controller',
  controllerService: 'controllerService',
  hermes: 'hermes',
  portal: 'portal',
};

export const emailsStringFormat = (emailsString: string) =>
  emailsString
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '');

export const transferSiteTypeToString = (type: number) => {
  switch (type) {
    case SiteType.rontoSite: {
      return t('portalSite.rontoSiteInfo');
    }
    case SiteType.controllerSite: {
      return t('portalSite.controllerSiteInfo');
    }
    case SiteType.rontoServiceSite: {
      return t('portalSite.rontoServiceSiteInfo');
    }
    default: {
      return t('portalSite.unknown');
    }
  }
};

export const addSiteIdPrefix = (path: string = '') => {
  const siteId = getItem('siteId');
  return siteId !== null ? `/sites/${getItem('siteId')}${path}` : path;
};

export const isJSXElement = (element: any) => {
  // 判斷是否為有效的React元素
  if (isValidElement(element)) {
    return true;
  }
  // 判斷是否具有特殊的屬性
  if (typeof element === 'object' && element !== null && element.hasOwnProperty('type') && element.hasOwnProperty('props')) {
    return true;
  }
  return false;
};

export const ipToInt = (ipv4: string) => {
  var buf = ipv4.split('.');
  return ((parseInt(buf[0]) << 24) | (parseInt(buf[1]) << 16) | (parseInt(buf[2]) << 8) | parseInt(buf[3])) >>> 0;
};

export const intToIp = (int: number) => {
  var part1 = int & 255;
  var part2 = (int >> 8) & 255;
  var part3 = (int >> 16) & 255;
  var part4 = (int >> 24) & 255;

  return part4 + '.' + part3 + '.' + part2 + '.' + part1;
};

export const secondsToHHMMSS = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds - hours * 3600) / 60);
  const secs = seconds - hours * 3600 - mins * 60;
  const timeString = hours.toString().padStart(2, '0') + ':' + mins.toString().padStart(2, '0') + ':' + secs.toString().padStart(2, '0');
  return timeString;
};

export const getSwitchDeviceTotalPorts = ({
  devAttrs,
  configDevAttr,
}: {
  devAttrs: number;
  configDevAttr: DeviceAttributesWithOutBitShifting;
}): number => {
  if (isDevAttrsMatch(devAttrs, configDevAttr.SwitchType8Port)) {
    return 10;
  } else if (isDevAttrsMatch(devAttrs, configDevAttr.SwitchType24Port)) {
    return 26;
  } else if (isDevAttrsMatch(devAttrs, configDevAttr.SwitchType48Port)) {
    return 52;
  }
  return 0;
};

export const getDeviceTypeText = (deviceType: number) => {
  switch (deviceType) {
    case DeviceType.Controller: {
      return t('settings.system.binInfo.controller');
    }
    case DeviceType.Eap: {
      return t('settings.system.binInfo.eap');
    }
    case DeviceType.Switch: {
      return t('settings.system.binInfo.switch');
    }
    default: {
      return t('portalSite.unknown');
    }
  }
};

export const getColor = (text: string) => {
  switch (text) {
    case 'Emergency':
    case 'Alert':
    case 'Critical':
      return 'text-[#FC3C2F]';
    case 'Error':
    case 'Warning':
      return 'text-[#F9A544]';
    case 'Notification':
    case 'Informational':
      return 'text-[#68CFFF]';
    case 'Debugging':
      return 'text-[#7FC83E]';
    default:
      return '';
  }
};

export function isValidVersionFormat(ver: string) {
  const reg = /^v?\d+\.\d+\.\d+$/;
  if (!ver || !reg.test(ver)) {
    return false;
  }
  return true;
}

export function compareVersion(v1: string, v2: string) {
  // v1 > v2 return 1, v1 < v2 return -1, v1 = v2 return 0, invalid format return undefined
  if (!isValidVersionFormat(v1) || !isValidVersionFormat(v2)) {
    return undefined;
  } else {
    const [x1, y1, z1] = v1.replace(/^v/, '').split('.');
    const [x2, y2, z2] = v2.replace(/^v/, '').split('.');

    if (x1 !== x2) {
      return parseInt(x1) > parseInt(x2) ? 1 : -1;
    }
    if (y1 !== y2) {
      return parseInt(y1) > parseInt(y2) ? 1 : -1;
    }
    if (z1 !== z2) {
      return parseInt(z1) > parseInt(z2) ? 1 : -1;
    }

    return 0;
  }
}

export const getSortedWifiBandsAndCbtMinMgmtRates = (wifiBands: string[], cbtMinMgmtRates: number[]) => {
  const bandOrder: Record<string, number> = {
    '2G': 1,
    '5G': 2,
    '6G': 3,
  };
  const paired = wifiBands.map((band, index) => ({
    band,
    rate: cbtMinMgmtRates[index] ?? 0,
  }));

  // 防呆：bandOrder 裡找不到的放最後不排序
  paired.sort((a, b) => (bandOrder[a.band] ?? 99) - (bandOrder[b.band] ?? 99));

  const sortedWifiBands = paired.map((item) => item.band);
  const sortedCbtMinMgmtRates = paired.map((item) => item.rate);
  return { sortedWifiBands, sortedCbtMinMgmtRates };
};

export const hasPortForwardChanges = (draft: ControllerQueryConfigDataDraft | null): boolean => {
  if (!draft) return false;
  const hasListChanges = draft.portForward?.some((item) => item.draftAction !== DraftAction.Unchanged) ?? false;
  const hasEnableChanges = draft.portForwardEnable !== draft.origPortForwardEnable;
  return hasListChanges || hasEnableChanges;
};

export const cleanDraftData = <T extends { draftAction: number }>(list: T[]): Omit<T, 'draftAction'>[] =>
  list.filter((item) => item.draftAction !== DraftAction.Deleted).map(({ draftAction, ...rest }) => rest as Omit<T, 'draftAction'>);

export const isDeepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => isDeepEqual(item, b[i]));
  }
  const aKeys = Object.keys(a as object).sort();
  const bKeys = Object.keys(b as object).sort();
  if (aKeys.length !== bKeys.length) return false;
  if (!aKeys.every((k, i) => k === bKeys[i])) return false;
  return aKeys.every((k) => isDeepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
};
